import { DataType, FormulaOperator } from '@doss/prisma/types'
import {
  type FormulaConditionInput,
  type FormulaInput,
  type FormulaValueInput,
} from '../antlr/utils/formula.js'

/**
 * The resolved scalar type a formula expression (or sub-expression) evaluates to.
 */
export const FormulaValueType = {
  NUMBER: DataType.NUMBER,
  STRING: DataType.STRING,
  DATE: DataType.DATE,
  DATE_TIME: DataType.DATE_TIME,
} as const
export type FormulaValueType = (typeof FormulaValueType)[keyof typeof FormulaValueType]

/**
 * The intentional fallback for formula nodes whose output type cannot be resolved during parsing.
 */
export const DEFAULT_FORMULA_OUTPUT_TYPE: FormulaValueType = FormulaValueType.NUMBER

/** The typed formula column subtypes. */
export const formulaTypes = [
  DataType.FORMULA_NUMBER,
  DataType.FORMULA_STRING,
  DataType.FORMULA_DATE,
  DataType.FORMULA_DATE_TIME,
] as const
export type FormulaType = (typeof formulaTypes)[number]

/**
 * Returns whether the data type is a formula type
 */
export function isFormulaType(type: DataType): type is FormulaType {
  return (formulaTypes as readonly DataType[]).includes(type)
}

/**
 * The scalar output type a formula column of the given type produces. Legacy FORMULA columns
 * predate typed outputs and are numeric.
 */
export function getFormulaOutputType(type: FormulaType): FormulaValueType {
  switch (type) {
    case DataType.FORMULA_NUMBER:
      return FormulaValueType.NUMBER
    case DataType.FORMULA_STRING:
      return FormulaValueType.STRING
    case DataType.FORMULA_DATE:
      return FormulaValueType.DATE
    case DataType.FORMULA_DATE_TIME:
      return FormulaValueType.DATE_TIME
  }
}

/**
 * Maps a column DataType to the formula value type it contributes as an operand. Text-like scalars
 * (ENUM, FILE_URL, USER, ID) all resolve to STRING. Lookups resolve through their target column's
 * type. Legacy FORMULA columns predate typed outputs and are always numeric.
 */
export function dataTypeToFormulaValueType(
  type: DataType,
  lookupColumnType?: DataType | null,
): FormulaValueType | undefined {
  switch (type) {
    case DataType.NUMBER:
    case DataType.FORMULA_NUMBER:
      return FormulaValueType.NUMBER
    case DataType.STRING:
    case DataType.ENUM:
    case DataType.FILE_URL:
    case DataType.USER:
    case DataType.ID:
    case DataType.FORMULA_STRING:
      return FormulaValueType.STRING
    case DataType.DATE:
    case DataType.FORMULA_DATE:
      return FormulaValueType.DATE
    case DataType.DATE_TIME:
    case DataType.FORMULA_DATE_TIME:
      return FormulaValueType.DATE_TIME
    case DataType.LOOKUP:
      return lookupColumnType !== null && lookupColumnType !== undefined
        ? dataTypeToFormulaValueType(lookupColumnType)
        : undefined
    default:
      return undefined
  }
}

/**
 * Maps a formula's resolved output type to the corresponding FORMULA_* DataType it should be persisted
 * as, so callers stop defaulting new formula columns/inputs to the legacy numeric-only FORMULA.
 */
export function formulaValueTypeToFormulaDataType(type: FormulaValueType): DataType {
  switch (type) {
    case FormulaValueType.NUMBER:
      return DataType.FORMULA_NUMBER
    case FormulaValueType.STRING:
      return DataType.FORMULA_STRING
    case FormulaValueType.DATE:
      return DataType.FORMULA_DATE
    case FormulaValueType.DATE_TIME:
      return DataType.FORMULA_DATE_TIME
  }
}

/**
 * Calendar units accepted by DATEADD / DATEDIFF. The unit argument must be a bare string
 * literal (not a column reference or nested formula) matching one of these, case-insensitively.
 */
export const DATE_UNITS = ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'] as const

export function isValidDateUnit(value: unknown): boolean {
  return typeof value === 'string' && DATE_UNITS.some((unit) => unit === value.toUpperCase())
}

/** The largest magnitude a numeric formula value may take (2^53) */
export const FORMULA_NUMERIC_MAX_ABS = 9007199254740992

/** Whether a number is finite and within {@link FORMULA_NUMERIC_MAX_ABS}. */
export function isFormulaNumericInRange(value: number): boolean {
  return Number.isFinite(value) && Math.abs(value) <= FORMULA_NUMERIC_MAX_ABS
}

/**
 * Whether a numeric literal's own text is within {@link FORMULA_NUMERIC_MAX_ABS}. Reads the digits
 * rather than parsing, since parsing rounds: `Number('9007199254740993')` is the bound itself.
 */
export function isFormulaNumericLiteralInRange(literal: string): boolean {
  const [integerDigits = '', fractionDigits = ''] = literal.replace(/^[-+]/, '').split('.')
  const magnitude = BigInt(integerDigits === '' ? '0' : integerDigits)
  const maximum = BigInt(FORMULA_NUMERIC_MAX_ABS)
  if (magnitude !== maximum) {
    return magnitude < maximum
  }
  // Exactly at the bound, so any non-zero fraction puts it over.
  return !/[1-9]/.test(fractionDigits)
}

export function formatNumericLiteralOutOfRangeError(literal: string): string {
  return (
    `${literal} is too large to use in a formula — numbers must be between ` +
    `-${FORMULA_NUMERIC_MAX_ABS} and ${FORMULA_NUMERIC_MAX_ABS}`
  )
}

/**
 * Argument shape of an operator:
 * - 'fixed': positional arguments, each with a set of allowed types
 * - 'variadic': one or more arguments that must all be of the allowed types; `sameType`
 *   additionally requires all arguments to resolve to one single type, so MIN/MAX cannot mix
 *   a date with a number
 * - 'arithmetic': the overloaded +,-,*,/ operators — resolved by resolveBinaryArithmetic
 * - 'unitLiteral': fixed leading arguments per `base`, plus a trailing calendar-unit string literal;
 *   `sameType` additionally requires all base arguments to resolve to one single type, so
 *   DATEDIFF cannot mix a date with a date & time
 *   `optionalFrom` is the first optional index that may be omitted
 */
export type FormulaArgsSpec =
  | { kind: 'fixed'; positions: FormulaValueType[][]; optionalFrom?: number }
  | { kind: 'variadic'; allowed: FormulaValueType[]; min: number; sameType?: boolean }
  | { kind: 'arithmetic' }
  | { kind: 'unitLiteral'; base: FormulaValueType[][]; sameType?: boolean }

/** Keywords for the filtered-aggregation forms of the column aggregation operators */
export type FilteredAggregationKeyword = 'SUMIF' | 'COUNTIF' | 'AVERAGEIF' | 'MINIFS' | 'MAXIFS'

export type FormulaOperatorSpec = {
  operator: FormulaOperator
  /** Whether the operator computes within a row, aggregates a column, or (MIN/MAX) can do both */
  level: 'row' | 'column' | 'both'
  /**
   * Functional family of the operator. Together with `level`, this must stay consistent with the
   * operator-classification arrays in utils/formula.ts
   */
  category: 'arithmetic' | 'rounding' | 'aggregation' | 'text' | 'date' | 'conversion'
  args: FormulaArgsSpec
  /**
   * 'arithmetic' is resolved per-operand-pair by resolveBinaryArithmetic; 'firstArg' returns the
   * resolved type of the first argument (DATEADD returns DATE for a DATE input, DATE_TIME for a
   * DATE_TIME input); 'commonArg' returns the single type shared by all resolvable arguments,
   * or undefined if they mix (MIN({date}) returns DATE, MIN({num}, 3) returns NUMBER).
   */
  returnType: FormulaValueType | 'firstArg' | 'arithmetic' | 'commonArg'
  /** Keyword used when the operator has a column operation filter (SUMIF, COUNTIF, MINIFS, MAXIFS) */
  filteredKeyword?: FilteredAggregationKeyword
  /**
   * Types the source column may resolve to when this operator is used as a COLUMN-LEVEL
   * aggregation (or its filtered MINIFS/MAXIFS form).
   */
  aggregatedColumnTypes?: FormulaValueType[]
}

const NUMBER = FormulaValueType.NUMBER
const STRING = FormulaValueType.STRING
const DATE = FormulaValueType.DATE
const DATE_TIME = FormulaValueType.DATE_TIME

const spec = (
  operator: FormulaOperator,
  level: FormulaOperatorSpec['level'],
  category: FormulaOperatorSpec['category'],
  args: FormulaArgsSpec,
  returnType: FormulaOperatorSpec['returnType'],
  filteredKeyword?: FilteredAggregationKeyword,
  aggregatedColumnTypes?: FormulaValueType[],
): FormulaOperatorSpec => ({
  operator,
  level,
  category,
  args,
  returnType,
  filteredKeyword,
  aggregatedColumnTypes,
})

/**
 * Single source of truth for every row/column formula operator: level, argument types, and return
 * type, per the Non-numeric Table Formulas PRD function catalog. Logic-expression operators
 * (EQUAL, CONTAINS, ...) are intentionally absent — they are validated by the logic-expression
 * checks, not the formula type system.
 */
export const formulaOperatorSpecs = {
  // Arithmetic operators — overloaded for date math, see resolveBinaryArithmetic
  [FormulaOperator.ADDITION]: spec(
    FormulaOperator.ADDITION,
    'row',
    'arithmetic',
    { kind: 'arithmetic' },
    'arithmetic',
  ),
  [FormulaOperator.MULTIPLICATION]: spec(
    FormulaOperator.MULTIPLICATION,
    'row',
    'arithmetic',
    { kind: 'arithmetic' },
    'arithmetic',
  ),
  [FormulaOperator.SUBTRACTION]: spec(
    FormulaOperator.SUBTRACTION,
    'row',
    'arithmetic',
    { kind: 'arithmetic' },
    'arithmetic',
  ),
  [FormulaOperator.DIVISION]: spec(
    FormulaOperator.DIVISION,
    'row',
    'arithmetic',
    { kind: 'arithmetic' },
    'arithmetic',
  ),
  // Rounding
  [FormulaOperator.ROUND]: spec(
    FormulaOperator.ROUND,
    'row',
    'rounding',
    // ROUND(n) rounds to the nearest whole number; ROUND(n, x) to x decimal digits (x < 0 rounds
    // left of the decimal point, matching Excel/Sheets)
    { kind: 'fixed', positions: [[NUMBER], [NUMBER]], optionalFrom: 1 },
    NUMBER,
  ),
  [FormulaOperator.CEIL]: spec(
    FormulaOperator.CEIL,
    'row',
    'rounding',
    { kind: 'fixed', positions: [[NUMBER]] },
    NUMBER,
  ),
  [FormulaOperator.FLOOR]: spec(
    FormulaOperator.FLOOR,
    'row',
    'rounding',
    { kind: 'fixed', positions: [[NUMBER]] },
    NUMBER,
  ),
  [FormulaOperator.MIN]: spec(
    FormulaOperator.MIN,
    'both',
    'aggregation',
    { kind: 'variadic', allowed: [NUMBER, DATE, DATE_TIME], min: 1, sameType: true },
    'commonArg',
    'MINIFS',
    [NUMBER, DATE, DATE_TIME],
  ),
  [FormulaOperator.MAX]: spec(
    FormulaOperator.MAX,
    'both',
    'aggregation',
    { kind: 'variadic', allowed: [NUMBER, DATE, DATE_TIME], min: 1, sameType: true },
    'commonArg',
    'MAXIFS',
    [NUMBER, DATE, DATE_TIME],
  ),
  [FormulaOperator.SUM]: spec(
    FormulaOperator.SUM,
    'column',
    'aggregation',
    { kind: 'variadic', allowed: [NUMBER], min: 1 },
    NUMBER,
    'SUMIF',
    [NUMBER],
  ),
  [FormulaOperator.COUNT]: spec(
    FormulaOperator.COUNT,
    'column',
    'aggregation',
    { kind: 'variadic', allowed: [NUMBER, STRING, DATE, DATE_TIME], min: 1 },
    NUMBER,
    'COUNTIF',
  ),
  [FormulaOperator.COUNT_DISTINCT]: spec(
    FormulaOperator.COUNT_DISTINCT,
    'column',
    'aggregation',
    { kind: 'variadic', allowed: [NUMBER, STRING, DATE, DATE_TIME], min: 1 },
    NUMBER,
  ),
  [FormulaOperator.AVERAGE]: spec(
    FormulaOperator.AVERAGE,
    'both',
    'aggregation',
    { kind: 'variadic', allowed: [NUMBER], min: 1 },
    NUMBER,
    'AVERAGEIF',
    [NUMBER],
  ),
  // The only aggregation taking more than one source column: it multiplies them element-wise per
  // source row, then sums. All operands must be columns on one source table — see
  // validateColumnFormulaInput.
  [FormulaOperator.SUMPRODUCT]: spec(
    FormulaOperator.SUMPRODUCT,
    'column',
    'aggregation',
    { kind: 'variadic', allowed: [NUMBER], min: 2 },
    NUMBER,
    undefined,
    [NUMBER],
  ),
  // Text functions
  [FormulaOperator.UPPER]: spec(
    FormulaOperator.UPPER,
    'row',
    'text',
    { kind: 'fixed', positions: [[STRING]] },
    STRING,
  ),
  [FormulaOperator.LOWER]: spec(
    FormulaOperator.LOWER,
    'row',
    'text',
    { kind: 'fixed', positions: [[STRING]] },
    STRING,
  ),
  [FormulaOperator.TRIM]: spec(
    FormulaOperator.TRIM,
    'row',
    'text',
    { kind: 'fixed', positions: [[STRING]] },
    STRING,
  ),
  [FormulaOperator.LEN]: spec(
    FormulaOperator.LEN,
    'row',
    'text',
    { kind: 'fixed', positions: [[STRING]] },
    NUMBER,
  ),
  [FormulaOperator.CONCAT]: spec(
    FormulaOperator.CONCAT,
    'row',
    'text',
    { kind: 'variadic', allowed: [STRING], min: 2 },
    STRING,
  ),
  [FormulaOperator.LEFT]: spec(
    FormulaOperator.LEFT,
    'row',
    'text',
    { kind: 'fixed', positions: [[STRING], [NUMBER]] },
    STRING,
  ),
  [FormulaOperator.RIGHT]: spec(
    FormulaOperator.RIGHT,
    'row',
    'text',
    { kind: 'fixed', positions: [[STRING], [NUMBER]] },
    STRING,
  ),
  [FormulaOperator.MID]: spec(
    FormulaOperator.MID,
    'row',
    'text',
    { kind: 'fixed', positions: [[STRING], [NUMBER], [NUMBER]] },
    STRING,
  ),
  [FormulaOperator.SUBSTITUTE]: spec(
    FormulaOperator.SUBSTITUTE,
    'row',
    'text',
    { kind: 'fixed', positions: [[STRING], [STRING], [STRING]] },
    STRING,
  ),
  // Date functions — accepts DATE_TIME operands wherever DATE is accepted
  [FormulaOperator.DATE]: spec(
    FormulaOperator.DATE,
    'row',
    'date',
    { kind: 'fixed', positions: [[NUMBER], [NUMBER], [NUMBER]] },
    DATE,
  ),
  [FormulaOperator.DATEADD]: spec(
    FormulaOperator.DATEADD,
    'row',
    'date',
    { kind: 'unitLiteral', base: [[DATE, DATE_TIME], [NUMBER]] },
    'firstArg',
  ),
  [FormulaOperator.DATEDIFF]: spec(
    FormulaOperator.DATEDIFF,
    'row',
    'date',
    {
      kind: 'unitLiteral',
      base: [
        [DATE, DATE_TIME],
        [DATE, DATE_TIME],
      ],
      sameType: true,
    },
    NUMBER,
  ),
  [FormulaOperator.YEAR]: spec(
    FormulaOperator.YEAR,
    'row',
    'date',
    { kind: 'fixed', positions: [[DATE, DATE_TIME]] },
    NUMBER,
  ),
  [FormulaOperator.MONTH]: spec(
    FormulaOperator.MONTH,
    'row',
    'date',
    { kind: 'fixed', positions: [[DATE, DATE_TIME]] },
    NUMBER,
  ),
  [FormulaOperator.DAY]: spec(
    FormulaOperator.DAY,
    'row',
    'date',
    { kind: 'fixed', positions: [[DATE, DATE_TIME]] },
    NUMBER,
  ),
  // Cross-type conversions
  [FormulaOperator.TO_TEXT]: spec(
    FormulaOperator.TO_TEXT,
    'row',
    'conversion',
    { kind: 'fixed', positions: [[NUMBER, STRING, DATE, DATE_TIME]] },
    STRING,
  ),
  [FormulaOperator.TO_NUMBER]: spec(
    FormulaOperator.TO_NUMBER,
    'row',
    'conversion',
    { kind: 'fixed', positions: [[STRING, NUMBER]] },
    NUMBER,
  ),
  [FormulaOperator.TO_DATE]: spec(
    FormulaOperator.TO_DATE,
    'row',
    'conversion',
    { kind: 'fixed', positions: [[STRING, DATE], [STRING]], optionalFrom: 1 },
    DATE,
  ),
} as const

export function getFormulaOperatorSpec(operator: FormulaOperator): FormulaOperatorSpec | undefined {
  return (formulaOperatorSpecs as Partial<Record<FormulaOperator, FormulaOperatorSpec>>)[operator]
}

/**
 * Filtered-aggregation keyword → base operator (SUMIF → SUM, MINIFS → MIN, ...), derived from each
 * spec's `filteredKeyword` so the two directions cannot drift.
 */
export const filteredKeywordToBaseOperator: Readonly<Record<string, FormulaOperator>> =
  Object.fromEntries(
    Object.values(formulaOperatorSpecs)
      .filter((operatorSpec) => operatorSpec.filteredKeyword !== undefined)
      .map((operatorSpec) => [operatorSpec.filteredKeyword, operatorSpec.operator]),
  )

/**
 * Which column types an operator may aggregate (column-level or filtered form). Returns the
 * allowed types on a violation, undefined when valid or unrestricted (COUNT). An undefined
 * `sourceType` counts as a violation — lenient callers should skip unknown types before calling.
 */
export function checkAggregatedSourceType(
  operator: FormulaOperator,
  sourceType: FormulaValueType | undefined,
): FormulaValueType[] | undefined {
  const allowed = getFormulaOperatorSpec(operator)?.aggregatedColumnTypes
  if (!allowed) {
    return undefined
  }
  return sourceType !== undefined && allowed.includes(sourceType) ? undefined : allowed
}

const TYPE_LABELS: Record<FormulaValueType, string> = {
  [FormulaValueType.NUMBER]: 'a number',
  [FormulaValueType.STRING]: 'text',
  [FormulaValueType.DATE]: 'a date',
  [FormulaValueType.DATE_TIME]: 'a date & time',
}

export function describeFormulaValueTypes(expected: FormulaValueType[]): string {
  return expected.map((type) => TYPE_LABELS[type]).join(' or ')
}

/**
 * Suggests the conversion function bridging an actual operand type to an expected one, so type
 * errors are actionable ("wrap it with TO_TEXT()") rather than just descriptive.
 */
function conversionHint(expected: FormulaValueType[], actual: FormulaValueType): string {
  if (expected.includes(FormulaValueType.STRING) && actual !== FormulaValueType.STRING) {
    return ' — convert it with TO_TEXT()'
  }
  if (expected.includes(FormulaValueType.DATE) && actual === FormulaValueType.STRING) {
    return ' — convert it with TO_DATE()'
  }
  if (expected.includes(FormulaValueType.NUMBER) && actual === FormulaValueType.STRING) {
    return ' — convert it with TO_NUMBER()'
  }
  return ''
}

export function formatArgumentTypeError(
  operator: FormulaOperator | FilteredAggregationKeyword,
  argIndex: number,
  expected: FormulaValueType[],
  actual: FormulaValueType,
): string {
  return (
    `${operator} expects ${describeFormulaValueTypes(expected)} for argument ${argIndex + 1}, ` +
    `but got ${TYPE_LABELS[actual]}${conversionHint(expected, actual)}`
  )
}

export function formatBranchTypeMismatchError(types: FormulaValueType[]): string {
  return (
    'All condition results must be the same type — got ' +
    types.map((type) => TYPE_LABELS[type]).join(' and ')
  )
}

function formatDateUnitError(operator: FormulaOperator): string {
  return `${operator} unit must be one of ${DATE_UNITS.join(', ')}`
}

function formatDateUnitLiteralError(operator: FormulaOperator): string {
  return `${operator} unit must be a text value like "DAY", not a column or formula`
}

function formatSameTypeError(operator: FormulaOperator, types: FormulaValueType[]): string {
  return (
    `${operator} arguments must be the same type — got ` +
    types.map((type) => TYPE_LABELS[type]).join(' and ')
  )
}

/**
 * Resolves the result type of a binary arithmetic operation, including the date overloads
 *  DATE ± NUMBER shifts by calendar days, DATE - DATE is the day difference. DATE_TIME is
 * rejected in arithmetic because a bare number has no unambiguous unit — DATEADD/DATEDIFF carry an
 * explicit unit instead. Unknown operand types resolve to unknown without an error.
 */
export function resolveBinaryArithmetic(
  operator: FormulaOperator,
  left: FormulaValueType | undefined,
  right: FormulaValueType | undefined,
): { type?: FormulaValueType; error?: string } {
  if (left === undefined || right === undefined) {
    return {}
  }

  if (left === FormulaValueType.STRING || right === FormulaValueType.STRING) {
    return { error: 'Text values cannot be used in arithmetic — use CONCAT to combine text' }
  }

  if (left === FormulaValueType.DATE_TIME || right === FormulaValueType.DATE_TIME) {
    return {
      error:
        'Date & time values cannot be used in arithmetic — use DATEADD or DATEDIFF with an ' +
        'explicit unit instead',
    }
  }

  const isMultiplicative =
    operator === FormulaOperator.MULTIPLICATION || operator === FormulaOperator.DIVISION
  if (isMultiplicative) {
    if (left === FormulaValueType.DATE || right === FormulaValueType.DATE) {
      return { error: 'Dates cannot be multiplied or divided' }
    }
    return { type: FormulaValueType.NUMBER }
  }

  if (operator === FormulaOperator.ADDITION) {
    if (left === FormulaValueType.DATE && right === FormulaValueType.DATE) {
      return { error: 'Two dates cannot be added together — use DATEDIFF to compare dates' }
    }
    if (left === FormulaValueType.DATE || right === FormulaValueType.DATE) {
      return { type: FormulaValueType.DATE }
    }
    return { type: FormulaValueType.NUMBER }
  }

  if (operator === FormulaOperator.SUBTRACTION) {
    if (left === FormulaValueType.DATE && right === FormulaValueType.DATE) {
      return { type: FormulaValueType.NUMBER }
    }
    if (left === FormulaValueType.DATE && right === FormulaValueType.NUMBER) {
      return { type: FormulaValueType.DATE }
    }
    if (right === FormulaValueType.DATE) {
      return { error: 'A date cannot be subtracted from a number' }
    }
    return { type: FormulaValueType.NUMBER }
  }

  return {}
}

/**
 * Collapses a list of resolved types to the single type they share. `type` is set when exactly
 * one known type appears; `mismatch` lists the distinct types when more than one appears (the
 * caller decides how to report it); both are absent when every input is unknown.
 */
export function commonKnownType(types: Array<FormulaValueType | undefined>): {
  type?: FormulaValueType
  mismatch?: FormulaValueType[]
} {
  const knownTypes = Array.from(
    new Set(types.filter((type): type is FormulaValueType => type !== undefined)),
  )
  if (knownTypes.length > 1) {
    return { mismatch: knownTypes }
  }
  return { type: knownTypes[0] }
}

export type FormulaTypeError = { message: string }

export type FormulaTypeRefResolver = (referenceId: string) => FormulaValueType | undefined

/**
 * When set, every formula node visited gets its resolved type written to `outputType`.
 */
export type InferOutputTypeOptions = {
  assignOutputTypes?: boolean
}

/**
 * Resolves the type of one formula value: a literal, a field reference, a nested formula, or a
 * conditional (IF/IFS-style) set of branches whose result types must agree.
 */
function inferFormulaValueType(
  value: FormulaValueInput,
  resolveRef: FormulaTypeRefResolver,
  errors: FormulaTypeError[],
  options?: InferOutputTypeOptions,
): FormulaValueType | undefined {
  if (value.conditions && value.conditions.length > 0) {
    return inferConditionBranchesType(value.conditions, resolveRef, errors, options)
  }
  if (value.formula) {
    return inferFormulaOutputType(value.formula, resolveRef, errors, options)
  }
  const referenceId = value.tableColumnId ?? value.formInputId
  if (referenceId) {
    return resolveRef(referenceId)
  }
  if (value.dateTimeToken === 'TODAY') {
    return FormulaValueType.DATE
  }
  if (value.dateTimeToken === 'NOW') {
    return FormulaValueType.DATE_TIME
  }
  if (typeof value.valueNumber === 'number' || typeof value.value === 'number') {
    return FormulaValueType.NUMBER
  }
  if (typeof value.valueString === 'string') {
    return FormulaValueType.STRING
  }
  return undefined
}

/**
 * All condition branches of a conditional value must resolve to the same type; that common type is
 * the value's type. Unknown branches are skipped (lenient), a genuine mismatch is an error.
 */
function inferConditionBranchesType(
  conditions: FormulaConditionInput[],
  resolveRef: FormulaTypeRefResolver,
  errors: FormulaTypeError[],
  options?: InferOutputTypeOptions,
): FormulaValueType | undefined {
  const branchTypes = conditions.map((condition) =>
    inferFormulaOutputType(condition.formula, resolveRef, errors, options),
  )
  const { type, mismatch } = commonKnownType(branchTypes)
  if (mismatch) {
    errors.push({ message: formatBranchTypeMismatchError(mismatch) })
  }
  return type
}

const isPlainStringLiteral = (value: FormulaValueInput): boolean =>
  typeof value.valueString === 'string' &&
  !value.dateTimeToken &&
  !value.tableColumnId &&
  !value.formInputId &&
  !value.formula &&
  (!value.conditions || value.conditions.length === 0)

/**
 * Infers the resolved output type of a formula tree, collecting type errors along the way.
 * Unknown reference types propagate as `undefined` without an error; an errored
 * sub-expression also resolves to `undefined` so a single mistake doesn't cascade.
 */
export function inferFormulaOutputType(
  input: FormulaInput,
  resolveRef: FormulaTypeRefResolver,
  errors: FormulaTypeError[] = [],
  options?: InferOutputTypeOptions,
): FormulaValueType | undefined {
  const resolved = resolveFormulaOutputType(input, resolveRef, errors, options)
  if (options?.assignOutputTypes) {
    input.outputType = resolved ?? DEFAULT_FORMULA_OUTPUT_TYPE
  }
  return resolved
}

function resolveFormulaOutputType(
  input: FormulaInput,
  resolveRef: FormulaTypeRefResolver,
  errors: FormulaTypeError[],
  options?: InferOutputTypeOptions,
): FormulaValueType | undefined {
  const operatorSpec = getFormulaOperatorSpec(input.operator)
  const orderedValues = [...input.formulaValues].sort((a, b) => a.order - b.order)
  const valueTypes = orderedValues.map((value) =>
    inferFormulaValueType(value, resolveRef, errors, options),
  )

  if (!operatorSpec) {
    // Skip for Comparison/logic operators (EQUAL, AND, CONTAINS, ...)
    // their operand rules live in checkValidLogicExpression (antlr/utils/formula.ts), run by
    // FormulaModelBuilder during parse.
    return undefined
  }

  if (operatorSpec.args.kind === 'arithmetic') {
    if (valueTypes.length === 0) {
      errors.push({ message: `${input.operator} requires at least 1 argument(s)` })
      return undefined
    }
    // A bare reference/literal is wrapped in a single-value ADDITION — no arithmetic involved
    if (valueTypes.length === 1) {
      return valueTypes[0]
    }
    return valueTypes.reduce((left, right) => {
      const result = resolveBinaryArithmetic(input.operator, left, right)
      if (result.error) {
        errors.push({ message: result.error })
        return undefined
      }
      return result.type
    })
  }

  const { errors: argErrors, resultType } = checkArgsAgainstSpec(
    operatorSpec,
    valueTypes,
    (index) => {
      const unitValue = orderedValues[index]
      return unitValue &&
        isPlainStringLiteral(unitValue) &&
        typeof unitValue.valueString === 'string'
        ? unitValue.valueString
        : undefined
    },
  )
  argErrors.forEach((argError) => errors.push({ message: argError.message }))
  return resultType
}

/**
 * Resolves an operator's return type from its spec and the resolved types of its arguments.
 * 'arithmetic' resolves to undefined here — arithmetic results are computed per-operand-pair by
 * resolveBinaryArithmetic instead.
 */
export function resolveSpecReturnType(
  spec: FormulaOperatorSpec,
  valueTypes: Array<FormulaValueType | undefined>,
): FormulaValueType | undefined {
  if (spec.returnType === 'firstArg') {
    return valueTypes[0]
  }
  if (spec.returnType === 'commonArg') {
    // A mismatch is intentionally not an error here — mixed argument types are already reported
    // as argument-type errors, so this only declines to resolve a return type
    return commonKnownType(valueTypes).type
  }
  if (spec.returnType === 'arithmetic') {
    return undefined
  }
  return spec.returnType
}

/** `argIndex` anchors the error to one argument; absent means the error covers the whole call */
export type SpecArgError = { argIndex?: number; message: string }

/**
 * Shared argument dispatch for variadic/fixed/unitLiteral operator specs, used by both the ANTLR
 * parse-tree checker (formula-type-checker.ts) and the FormulaInput checker
 * (inferFormulaOutputType) so their rules cannot drift. `argTypes` are the caller-resolved operand
 * types in order; `resolveUnitLiteral` returns the bare string literal at an index (the
 * DATEADD/DATEDIFF unit) or undefined when that argument is not a plain literal.
 */
export function checkArgsAgainstSpec(
  spec: FormulaOperatorSpec,
  argTypes: Array<FormulaValueType | undefined>,
  resolveUnitLiteral: (index: number) => string | undefined,
): { errors: SpecArgError[]; resultType: FormulaValueType | undefined } {
  // Arithmetic operators are not checked here: their operand rules depend on the operand pair
  // (DATE + NUMBER is valid, DATE + DATE is not), so both callers resolve them pairwise via
  // resolveBinaryArithmetic instead of against a positional spec
  if (spec.args.kind === 'arithmetic') {
    return { errors: [], resultType: undefined }
  }

  if (spec.args.kind === 'variadic') {
    if (argTypes.length < spec.args.min) {
      return {
        errors: [{ message: `${spec.operator} requires at least ${spec.args.min} argument(s)` }],
        resultType: undefined,
      }
    }
    const { allowed } = spec.args
    const errors: SpecArgError[] = []
    argTypes.forEach((argType, index) => {
      if (argType !== undefined && !allowed.includes(argType)) {
        errors.push({
          argIndex: index,
          message: formatArgumentTypeError(spec.operator, index, allowed, argType),
        })
      }
    })
    // MIN/MAX admit numbers and dates but not a mix — unknown operand types are skipped, only a
    // proven mismatch errors
    if (spec.args.sameType) {
      const { mismatch } = commonKnownType(argTypes)
      if (mismatch) {
        errors.push({ message: formatSameTypeError(spec.operator, mismatch) })
      }
    }
    return {
      errors,
      resultType: errors.length > 0 ? undefined : resolveSpecReturnType(spec, argTypes),
    }
  }

  const positions = spec.args.kind === 'unitLiteral' ? spec.args.base : spec.args.positions
  // unitLiteral's trailing calendar-unit argument is always required, hence the +1 with no minimum
  // of its own. A 'fixed' spec's `optionalFrom` is the first omittable index, so ROUND's and
  // TO_DATE's two positions accept one or two arguments.
  const maxCount = spec.args.kind === 'unitLiteral' ? positions.length + 1 : positions.length
  const minCount = spec.args.kind === 'fixed' ? (spec.args.optionalFrom ?? maxCount) : maxCount
  if (argTypes.length < minCount || argTypes.length > maxCount) {
    const requirement =
      minCount === maxCount ? `exactly ${maxCount}` : `between ${minCount} and ${maxCount}`
    return {
      errors: [{ message: `${spec.operator} requires ${requirement} argument(s)` }],
      resultType: undefined,
    }
  }

  const errors: SpecArgError[] = []
  positions.forEach((expected, index) => {
    const argType = argTypes[index]
    if (argType !== undefined && !expected.includes(argType)) {
      errors.push({
        argIndex: index,
        message: formatArgumentTypeError(spec.operator, index, expected, argType),
      })
    }
  })

  if (spec.args.kind === 'unitLiteral') {
    // Each base position admits DATE or DATE_TIME independently, so a mixed pair passes the
    // per-position loop above; `sameType` closes that gap. Unknown operand types are skipped —
    // only a proven mismatch errors.
    if (spec.args.sameType) {
      const { mismatch } = commonKnownType(argTypes.slice(0, positions.length))
      if (mismatch) {
        errors.push({ message: formatSameTypeError(spec.operator, mismatch) })
      }
    }
    const unitIndex = positions.length
    const unit = resolveUnitLiteral(unitIndex)
    if (unit === undefined) {
      errors.push({ argIndex: unitIndex, message: formatDateUnitLiteralError(spec.operator) })
    } else if (!isValidDateUnit(unit)) {
      errors.push({ argIndex: unitIndex, message: formatDateUnitError(spec.operator) })
    }
  }

  return {
    errors,
    resultType: errors.length > 0 ? undefined : resolveSpecReturnType(spec, argTypes),
  }
}
